import React, { useState } from 'react';
import { Modal } from '../ui/Modal/Modal';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { useNotificationStore } from '@/stores/notificationStore';
import { apiFetch } from '@/lib/api';
import { PASSWORD_CHECKS } from '@/lib/passwordPolicy';

interface SetMemberPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  userId: string;
  userEmail: string;
  userName?: string;
}

export function SetMemberPasswordModal({
  isOpen,
  onClose,
  orgId,
  userId,
  userEmail,
  userName,
}: SetMemberPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allChecksPassed = PASSWORD_CHECKS.every(c => c.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const displayName = userName || userEmail;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allChecksPassed || !passwordsMatch) return;
    setError(null);
    setIsLoading(true);
    try {
      const res = await apiFetch(`/api/orgs/${orgId}/members/${userId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error?.message || 'Failed to update password');
      }
      useNotificationStore.getState().success(`Password updated for ${displayName}`);
      setPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setConfirmPassword('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Set Password for ${displayName}`}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            This will immediately change the password for <strong>{displayName}</strong> and sign them out of all active sessions.
          </p>
        </div>

        <Input
          label="New Password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="••••••••••••"
          required
        />

        {password.length > 0 && (
          <div className="space-y-1.5">
            {PASSWORD_CHECKS.map((check, i) => {
              const passed = check.test(password);
              return (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={passed ? 'text-green-600' : 'text-muted-foreground'}>
                    {passed ? '✓' : '○'}
                  </span>
                  <span className={passed ? 'text-green-600' : 'text-muted-foreground'}>
                    {check.label()}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <Input
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          placeholder="••••••••••••"
          required
        />

        {confirmPassword.length > 0 && !passwordsMatch && (
          <p className="text-xs text-red-600">Passwords do not match</p>
        )}

        {error && <Alert variant="error">{error}</Alert>}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!allChecksPassed || !passwordsMatch}
            loading={isLoading}
          >
            Set Password
          </Button>
        </div>
      </form>
    </Modal>
  );
}
